import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@mui/material';
import { Button } from '@repo/videa-components/src/ui/button';
import classNames from 'classnames';
import { format, formatDistanceToNow } from 'date-fns';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import type { TImagetype } from '../../../@types/global';
import { ImageType } from '../../../@types/global';
import { VideaButton } from '../../../common-components/elements/VideaButton/VideaButton';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import { useLatestImagesV2ToV1 } from '../../../react-query/useLatestImagesv2';
import { usePatientVisits } from '../../../react-query/usePatientVisits';
import {
  black,
  blueBlack400,
  blueBlack630,
  blueBlack700,
  blueUltramarine050,
  blueUltramarine200,
  greyLight400,
  yellowOrange300,
} from '../../../theme/videa-style-tokens';
import { useIsVDAPracticeWithNonVDAAnalysis } from '../../../utils/hooks/useIsVDAPracticeWithNonVDAAnalysis';
import { useMapImageTypeToLabel } from '../../../utils/string';
import { colors } from '../../../utils/theme';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../../utils/user';
import { useRotation } from '../../annotator/multiFileViewer/RotationContext';
import type { ImageObject } from '../../annotator/types';
import { isDirectorUser } from '../../login/login-utils';
import { InfoOutlinedIcon } from '../../patientList/patientStrip/findings/styles';
import { Image } from '../image/Image';

import {
  findImageWithMaxMatchingToothNumbers,
  hasOutdatedRblAnalysis,
} from './utils';

const ImageTypesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.125em;
  border: 1px solid ${blueBlack700};
  border-radius: 0.25em;
  overflow: hidden;

  .visit-image-type-filter {
    .MuiButton-root {
      font-weight: 500;
      padding: 0 0.75em 0 0;
      margin: 0;
      border-width: 0;
      border-image-width: 0;
      border-radius: 0;
      gap: 0.125em;
      height: 3em;

      .MuiBadge-root {
        min-width: 1em;

        .MuiBadge-badge {
          background-color: ${greyLight400};
          color: ${black};
        }
      }

      &.filtered-out {
        background-color: ${black};
        color: ${blueBlack400};

        .MuiBadge-badge {
          background-color: ${blueBlack700};
          color: ${blueBlack400};
        }
      }
      &.included {
        background-color: ${black};
        color: ${blueUltramarine050};
      }
    }
  }
`;

const LastVisitContainerOuter = styled(Box)`
  width: 100%;
  padding: 0 2.5em;
  display: flex;
  flex-direction: column;

  .images-container {
    margin: 0;
    display: flex;
    align-items: center;
    border: 2px solid ${blueBlack700};
    border-radius: 0.5em;
  }
  .image-section {
    height: 28vw;
    width: 28vw;
    max-width: calc(100% - 5em);
    flex-grow: 1;
    position: relative;
    border-radius: 0.5em;
    overflow: hidden;
    .default {
      cursor: pointer !important;
      &.user-annotation {
        cursor: crosshair !important;
      }
    }
  }
  .visit-header {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 1em;
  }
  .warning {
    fill: ${yellowOrange300};
    margin: auto;
  }
`;

const LastVisitSelectContainerInner = styled(Box)`
  gap: 0.5em;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1em 0;
`;

const LastVisitSelect = styled(Select)`
  color: ${colors.WHITE};
  font-size: 1em;
  .MuiSelect-select,
  .MuiSvgIcon-root {
    color: ${blueUltramarine050};
  }
  .MuiSelect-select {
    background: ${blueBlack630};
    padding: 0.75em;
  }
  .MuiInputBase-root {
    font-size: 1em;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 0.75em;
  font-weight: 700;
`;

const StyledMenuSubheader = styled(ListSubheader)`
  font-size: 0.625em;
  color: ${colors.MID_GREY_2};
  line-height: 1.875em;
`;

const AdditionalImagesSection = styled(Box)`
  color: ${colors.WHITE};
  padding-bottom: 1em;
  width: 7em;
  height: 28vw;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background-color: ${blueBlack700};
`;

const AdditionalImagesContainerInner = styled(Box)`
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  gap: 0.5em;

  .image {
    flex-shrink: 0;
    border-color: transparent;
    border-radius: 0.25em;
    border-width: 0.125em;
    border-style: solid;
    height: 5em;
    width: 6.25em;
    padding: 0.25em;
    box-sizing: border-box;
  }
  .selected-image {
    border-color: ${blueUltramarine200};
  }
`;

const NoImagesText = styled(Box)`
  color: ${colors.WHITE};
`;

const OutdatedAnalysisContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const OutdatedAnalysisWarning = styled(Box)`
  color: ${colors.ORANGE};
`;

const OutdatedAnalysisNotification = styled(Box)`
  color: ${colors.ORANGE};
  padding-left: 0.5em;
`;

export const ImageHistorySection = ({
  section,
  activeImage,
}: {
  section: 'left' | 'right';
  activeImage: ImageObject | null;
}) => {
  // params
  const { id: patientId } = useParams<{ id: string }>();

  // state
  // By default, the image in the right side of the modal should match the image in the single image viewer
  const [selectedImageId, setSelectedImageId] = useState(activeImage?.imageId);
  const [selectedVisit, setSelectedVisit] = useState<number>(-1);

  // hooks
  const user = useSelector((state: any) => state?.login?.user);
  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;
  const mapImageTypeToLabel = useMapImageTypeToLabel();
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();

  const { data: patientVisitsData, isLoading: isLoadingVisits } =
    usePatientVisits({
      patientId,
    });

  const currentVisit = patientVisitsData?.visits.find(
    (visit) => visit.id === selectedVisit,
  );

  const patientVideaIdForImages = currentVisit?.patientVideaId || patientId;
  const { data: latestImagesForVisit, isLoading: isLoadingImages } =
    useLatestImagesV2ToV1({
      videaPatientId: patientVideaIdForImages,
      estimatedVisitId: String(selectedVisit),
      options: {
        onSuccess: (latestImagesResult) => {
          if (latestImagesResult.analyzedImages.length) {
            const latestImages = latestImagesResult.analyzedImages;
            const isNewResultHasExistingImageId = latestImages.some(
              (image) => image.imageId === selectedImageId,
            );
            if (!isNewResultHasExistingImageId) {
              if (activeImage?.imageId && section === 'left') {
                const idealToothArray =
                  activeImage.models?.tooth_identification?.findings.map(
                    (finding) => finding.toothNumber,
                  ) || [];
                // find image with max number of matching tooth ids to display in the left section
                const imageIdWithMatchingToothNos =
                  findImageWithMaxMatchingToothNumbers(
                    idealToothArray,
                    latestImages,
                  );
                setSelectedImageId(imageIdWithMatchingToothNos);
                return;
              }
              setSelectedImageId(latestImages[0].imageId);
            }
            return;
          }
          setSelectedImageId(undefined);
        },
      },
    });
  const isVDAPracticeWithNonVDAAnalysis = useIsVDAPracticeWithNonVDAAnalysis(
    latestImagesForVisit?.analyzedImages,
  );
  const isLeftSectionVDAPracticeWithNonVDAAnalysis =
    section === 'left' && isVDAPracticeWithNonVDAAnalysis;

  const latestImageCounts: { [Property in TImagetype]?: number } = {};
  latestImagesForVisit?.analyzedImages.forEach((image) => {
    let imageType: typeof image.detectedImageType | 'OTHER' =
      image.detectedImageType;
    // coerce null/undefined to other
    if (!imageType) {
      imageType = ImageType.OTHER;
    }
    latestImageCounts[imageType] = (latestImageCounts[imageType] || 0) + 1;
  });
  const [filteredOutImageTypes, setFilteredOutImageTypes] = useState<
    TImagetype[]
  >([ImageType.COLOR_IMAGE]);
  const handleImageTypeFilter = (imageType: TImagetype) => {
    if (filteredOutImageTypes.includes(imageType)) {
      setFilteredOutImageTypes(
        filteredOutImageTypes.filter((type) => type !== imageType),
      );
    } else {
      setFilteredOutImageTypes([...filteredOutImageTypes, imageType]);
    }
  };

  const {
    saveAndRerunVisit,
    currentlyRotatingImages,
    rotatedImages,
    isLoadingRerun,
  } = useRotation();

  const hasOutdatedRbl = hasOutdatedRblAnalysis(
    latestImagesForVisit?.analyzedImages,
  );
  const lastAnalysisDateForSelectedVisit =
    latestImagesForVisit?.analyzedImages[0]?.updatedAt;
  const visitAnalysisDate = lastAnalysisDateForSelectedVisit
    ? format(new Date(lastAnalysisDateForSelectedVisit), 'P', {
        locale: dateLocale,
      })
    : '';

  // Sets the default selected visit upon receiving new or initial patient visits
  useEffect(() => {
    if (!patientVisitsData?.visits.length) {
      return;
    }

    // Assuming we have multiple visits, default the visit to "Last Visit" or index 1
    if (
      patientVisitsData.visits.length > 1 &&
      section === 'left' &&
      patientVisitsData.visits[1]?.id
    ) {
      setSelectedVisit(patientVisitsData.visits[1].id);
    } else {
      // Otherwise, or if this is the right visit, default to the "Current Visit" or index 0
      patientVisitsData.visits[0]?.id &&
        setSelectedVisit(patientVisitsData.visits[0]?.id);
    }
  }, [section, patientVisitsData]);

  const selectedImage = latestImagesForVisit?.analyzedImages.find(
    (image) => image.imageId === selectedImageId,
  );

  useEffect(() => {
    if (
      filteredOutImageTypes.includes(ImageType.COLOR_IMAGE) &&
      selectedImage?.detectedImageType === ImageType.COLOR_IMAGE
    ) {
      setFilteredOutImageTypes(
        filteredOutImageTypes.filter((type) => type !== 'COLOR_IMAGE'),
      );
    }
  }, [filteredOutImageTypes, selectedImage?.detectedImageType]);

  const visitOptions = patientVisitsData?.visits.reduce(
    // @ts-expect-error TS(2769): No overload matches this call.
    (accumulator, visit, index) => {
      const date = new Date(visit.visitDate || '');

      const menuItem = visit.id ? (
        <StyledMenuItem
          key={visit.id}
          value={visit.id}
          
        >
          {format(date, 'dd-MMM-yyyy', {
            locale: dateLocale,
          })}
        </StyledMenuItem>
      ) : undefined;

      if (index === 0 || index === 1) {
        return [
          ...accumulator,
          <StyledMenuSubheader >
            {index === 0 ? t`Recent` : t`Past`} <Trans>Visits</Trans>
          </StyledMenuSubheader>,
          menuItem,
        ];
      }
      return [...accumulator, menuItem];
    },
    [],
  );

  const additionalImages = latestImagesForVisit?.analyzedImages.map(
    (image, index) => {
      if (
        filteredOutImageTypes &&
        filteredOutImageTypes.includes(
          image.detectedImageType || ImageType.OTHER,
        )
      ) {
        return;
      }
      return (
        <ButtonBase
          data-testid="add-image"
          className={classNames('image', {
            'selected-image': image.imageId === selectedImageId,
          })}
          onClick={() => {
            setSelectedImageId(image.imageId);
          }}
          key={image.imageId}
        >
          <Image
            activeImage={image}
            additionalImage
            section={section}
            index={index}
          />
        </ButtonBase>
      );
    },
  );

  const hasNoImages =
    latestImagesForVisit?.analyzedImages.length === 0 &&
    !isLoadingImages &&
    !isLoadingVisits;

  const allImagesForReanalysis = latestImagesForVisit?.analyzedImages?.map(
    (image) => {
      return {
        imageId: image.imageId,
        rotation: 360,
      };
    },
  );

  const isRestrictedFromEditing = isDirectorUser(user);
  const shouldShowRerun =
    section === 'left' &&
    hasOutdatedRbl &&
    !isSalesDemo &&
    !hasNoImages &&
    !isRestrictedFromEditing;

  const shouldShowRotationRerun =
    selectedImageId &&
    currentlyRotatingImages?.imageIds.includes(selectedImageId) &&
    !isRestrictedFromEditing &&
    !isSalesDemo &&
    // Rerun analysis endpoint is behaving inconsistently, disable for now
    false;

  const handleTriggerRerun = () => {
    if (
      (!allImagesForReanalysis && !shouldShowRotationRerun) ||
      selectedVisit === -1 ||
      !selectedImageId
    ) {
      return;
    }
    // If we are rerunning all images pass through all images
    if (
      allImagesForReanalysis &&
      (shouldShowRerun || isLeftSectionVDAPracticeWithNonVDAAnalysis)
    ) {
      saveAndRerunVisit(allImagesForReanalysis, selectedVisit);
      return;
    }
    // Otherwise just pass the currently selected image
    saveAndRerunVisit(
      [
        {
          imageId: selectedImageId,
          rotation: rotatedImages[selectedImageId] * -1,
        },
      ],

      selectedVisit,
    );
  };

  const selectedDate = patientVisitsData?.visits.find(
    (visit) => visit.id === selectedVisit,
  )?.visitDate;

  const isColorImageFilteredOut = filteredOutImageTypes.includes(
    ImageType.COLOR_IMAGE,
  );

  const handleColorImages = (checked: boolean) => {
    if (checked) {
      // no color images visible
      setFilteredOutImageTypes([
        ...filteredOutImageTypes,
        ImageType.COLOR_IMAGE,
      ]);
      setSelectedImageId(
        () =>
          latestImagesForVisit?.analyzedImages.find(
            (image) => image.detectedImageType !== ImageType.COLOR_IMAGE,
          )?.imageId,
      );
    } else {
      setFilteredOutImageTypes(
        filteredOutImageTypes.filter((type) => type !== ImageType.COLOR_IMAGE),
      );
    }
  };

  return (
    <LastVisitContainerOuter >
      <LastVisitSelectContainerInner >
        <div className="visit-header">
          <LastVisitSelect
            aria-label="visit-selection"
            
            data-testid="dropdown-visit-dates"
            value={selectedVisit}
            onChange={(e) => {
              if (e.target.value !== undefined) {
                setSelectedVisit(e.target.value as number);
              }
            }}
          >
            {/** @ts-expect-error TS(2322): Type 'Visit | undefined' is not assignable to type... Remove this comment to see the full error message */}
            {visitOptions}
          </LastVisitSelect>
          {section === 'left' && isLoadingRerun && <CircularProgress />}
          {(shouldShowRerun ||
            shouldShowRotationRerun ||
            isLeftSectionVDAPracticeWithNonVDAAnalysis) && (
            <VideaButton onClick={handleTriggerRerun}>Rerun AI</VideaButton>
          )}

          {section === 'left' &&
            !hasOutdatedRbl &&
            !isSalesDemo &&
            !hasNoImages && (
              <Tooltip
                title={t`This visit was analyzed on ${visitAnalysisDate} and is up to date.`}
              >
                <InfoOutlinedIcon />
              </Tooltip>
            )}

          {selectedDate && (
            <VideaTypography variant="bodySmall" color={blueUltramarine050}>
              {isSalesDemo && section === 'right'
                ? t`10 minutes ago`
                : `${formatDistanceToNow(new Date(selectedDate), {
                    locale: dateLocale,
                    addSuffix: true,
                  })}`}
            </VideaTypography>
          )}
          {!!latestImageCounts[ImageType.COLOR_IMAGE] && (
            <div>
              <Switch
                sx={{
                  '& .MuiSwitch-track': {
                    bgcolor: 'grey',
                  },
                }}
                color="info"
                checked={!isColorImageFilteredOut}
                onChange={(_, checked) => {
                  handleColorImages(!checked);
                }}
              />

              <VideaTypography
                style={{ color: 'white' }}
                variant="labelSmall"
                className="toggle-label"
              >
                <Trans>Dental photos</Trans>
              </VideaTypography>
            </div>
          )}
          <ImageTypesContainer data-testid="types-container">
            {Object.entries(latestImageCounts)
              .filter(([imageType]) => imageType !== ImageType.COLOR_IMAGE)
              .map(([imageType, count]) => (
                <div className="visit-image-type-filter">
                  <Button
                    onClick={() =>
                      handleImageTypeFilter(imageType as TImagetype)
                    }
                    variant="ghost"
                    className={
                      filteredOutImageTypes.includes(imageType as TImagetype)
                        ? 'filtered-out'
                        : 'included'
                    }
                    key={imageType}
                  >
                    {`${mapImageTypeToLabel[imageType as TImagetype]}`}
                    <Badge badgeContent={count} color="info" />
                  </Button>
                  <br />
                </div>
              ))}
          </ImageTypesContainer>
        </div>
      </LastVisitSelectContainerInner>
      <div className="images-container">
        {!!latestImagesForVisit?.analyzedImages.length && (
          <AdditionalImagesSection
            
            data-testid="additional-images"
          >
            <AdditionalImagesContainerInner>
              {additionalImages}
            </AdditionalImagesContainerInner>
          </AdditionalImagesSection>
        )}

        <div className="image-section">
          {section === 'left' &&
            (hasOutdatedRbl || isLeftSectionVDAPracticeWithNonVDAAnalysis) &&
            !isSalesDemo &&
            !hasNoImages && (
              <OutdatedAnalysisContainer>
                <OutdatedAnalysisWarning />
                <OutdatedAnalysisNotification>
                  This visit was analyzed on {visitAnalysisDate} and may have
                  outdated{' '}
                  {isLeftSectionVDAPracticeWithNonVDAAnalysis
                    ? 'AI'
                    : 'measurements or not include newer finding types'}
                  . Please rerun AI for best comparison results.
                </OutdatedAnalysisNotification>
              </OutdatedAnalysisContainer>
            )}
          {hasNoImages && (
            <NoImagesText>No images found for this visit</NoImagesText>
          )}
          {selectedImageId && (
            <Image activeImage={selectedImage} section={section} />
          )}
        </div>
      </div>
    </LastVisitContainerOuter>
  );
};
